import {
  AudioWaveform,
  Blend,
  Book,
  BookImage,
  BookOpen,
  BookOpenText,
  Bot,
  Box,
  Boxes,
  Briefcase,
  Brush,
  Building2,
  Camera,
  ChartArea,
  ChartBar,
  ChartColumn,
  ChartPie,
  CircleUserRound,
  Clapperboard,
  Cloud,
  Compass,
  Contact,
  Database,
  DatabaseZap,
  FileImage,
  FileMusic,
  FileSearch,
  FileText,
  FileType,
  Film,
  Fingerprint,
  Flag,
  FlaskConical,
  Gamepad2,
  Gem,
  Gift,
  Guitar,
  Hash,
  Headphones,
  IdCard,
  Image,
  ImagePlay,
  Laugh,
  Layers,
  Leaf,
  Library,
  Lightbulb,
  Mail,
  MailOpen,
  Megaphone,
  MessageSquareCode,
  MessagesSquare,
  Mic,
  MicVocal,
  MonitorPlay,
  Music,
  Newspaper,
  Package,
  PaintBucket,
  Palette,
  PanelsTopLeft,
  Pencil,
  PenLine,
  PenTool,
  PersonStanding,
  Piano,
  Plug,
  Podcast,
  Presentation,
  Projector,
  Rabbit,
  Rotate3d,
  ScrollText,
  Search,
  Shapes,
  Sheet,
  Shirt,
  SlidersVertical,
  Smartphone,
  Sparkles,
  Sprout,
  SquarePlay,
  Stamp,
  Tag,
  Tags,
  Target,
  Theater,
  Video,
  WandSparkles,
} from 'lucide-react';

// Near-duplicate skills that should share another skill's icon. Merging the
// skills themselves is out of scope.
export const SKILL_ICON_ALIASES = {
  'amazon-web-services': 'aws',
  'corel-draw': 'coreldraw',
  golang: 'go',
  'motion-graphics': 'motion-graphic',
  nodejs: 'node.js',
  'unity-3d': 'unity',
};

// Discipline and concept skills have no brand mark, so they get a Lucide
// glyph. Covers skills held by at least 10,000 freelancers (Oct 2026 export).
export const SKILL_GLYPHS = {
  '2d-animation': Layers,
  '3d-animation': Rotate3d,
  '3d-design': Box,
  '3d-model': Boxes,
  'advert-design': Tag,
  'ai-collection-video': Bot,
  'ai-data-labelling': Tags,
  animation: WandSparkles,
  api: Plug,
  'app-design': Smartphone,
  artwork: Palette,
  audio: Headphones,
  banner: Flag,
  'book-cover': Book,
  'book-illustration': BookImage,
  'brand-identity': Fingerprint,
  branding: Gem,
  brochure: BookOpen,
  'business-card': IdCard,
  cartoon: Laugh,
  catalogue: Library,
  'character-design': PersonStanding,
  'childrens-book-illustration': Rabbit,
  composition: Piano,
  copywriting: PenLine,
  'corporate-video': Building2,
  crm: Contact,
  'data-science': FlaskConical,
  'data-visualization': ChartPie,
  'editorial-illustration': Newspaper,
  'email-design': MailOpen,
  'email-marketing': Mail,
  'explainer-video': Lightbulb,
  'fashion-design': Shirt,
  flyer: FileImage,
  'freehand-digital-illustration': Pencil,
  'game-design': Gamepad2,
  gif: ImagePlay,
  'greetings-card': Gift,
  icon: Shapes,
  illustration: Brush,
  infographic: ChartColumn,
  instrumental: Guitar,
  leadership: Compass,
  letterhead: FileText,
  logo: PenTool,
  magazine: BookOpenText,
  master: AudioWaveform,
  mix: SlidersVertical,
  'motion-graphic': Blend,
  'music-theory': FileMusic,
  'music-video': MonitorPlay,
  photo: Camera,
  'podcast-production': Podcast,
  portrait: CircleUserRound,
  postcard: Stamp,
  poster: Image,
  'presentation-design': Presentation,
  'product-design': Package,
  'product-video': Video,
  'promo-video': Megaphone,
  'prompt-engineering': MessageSquareCode,
  script: ScrollText,
  seo: Search,
  'seo-copywriting': FileSearch,
  'set-design': Theater,
  'short-film': Film,
  'social-media-design': Hash,
  soundtrack: Music,
  sql: Database,
  't-shirt-design': Shirt,
  'video-advert': Clapperboard,
  vocal: MicVocal,
  voiceover: Mic,
  'web-design': PanelsTopLeft,

  // Tools whose owners publish no usable standalone mark.
  'adobe-after-effects': Sparkles,
  'cap-cut': SquarePlay,
  chatgpt: MessagesSquare,
  'google-ads': Target,
  'google-cloud': Cloud,
  'microsoft-excel': Sheet,
  'microsoft-office': Briefcase,
  'microsoft-powerpoint': Projector,
  'microsoft-word': FileType,
  mongodb: Leaf,
  'power-bi': ChartBar,
  redis: DatabaseZap,
  spring: Sprout,
  'substance-painter': PaintBucket,
  tableau: ChartArea,
};

// Slugs in the database aren't always clean: "power-bi " has a trailing space
// and "Beats" is capitalised.
export const normaliseSkillSlug = (slug) => {
  const clean = (slug || '').trim().toLowerCase();

  return SKILL_ICON_ALIASES[clean] || clean;
};

const getBundledLogo = (slug) => {
  try {
    return require(`static/images/portfolios/skills/${slug}.png`).default;
  } catch (err) {
    return null;
  }
};

/**
 * A brand logo from the bundled set where there is one, else a Lucide glyph
 * for discipline skills, else null so the caller shows the letter tile.
 */
export const getSkillIcon = (skill) => {
  const slug = normaliseSkillSlug(skill?.slug);

  if (!slug) {
    return null;
  }

  const logo = getBundledLogo(slug);

  if (logo) {
    return { logo };
  }

  return SKILL_GLYPHS[slug] ? { Glyph: SKILL_GLYPHS[slug] } : null;
};
